import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import { useAuth } from '../context/AuthContext';
import { fetchEditors, removeEditor, addOrUpdateEditor, sendProfessionalEditorInvitation } from '../graphqlService';
import { EDITOR_CONFIG } from '../config/uploadConfig';
import { brand } from '../theme';
import { debugLog } from '../utils/debugLog';
import PageContainer from './ui/PageContainer';
import PageHeader from './ui/PageHeader';
import Section from './ui/Section';
import StatusChip from './ui/StatusChip';
import EmptyState from './ui/EmptyState';
import LoadingState from './ui/LoadingState';
import {
  buildInvitationLink,
  canResendInvitation,
  EditorRecord,
  formatEditorSummary,
  getInvitationStatusMeta,
  isAtEditorLimit,
  isSelfInvitationError,
  readableEditorError,
  summarizeEditors,
  validateInviteEmail,
} from './Editors/editorModel';

const LOAD_ERROR = "We couldn't load your editors. Check your connection and try again.";
const SESSION_ENDED = 'Your session has ended. Sign in again to continue.';
const INVITE_COPY = "We'll email them a link to join. It expires in 7 days.";

type ToastSeverity = 'success' | 'error' | 'warning' | 'info';

interface ToastState {
  open: boolean;
  message: string;
  severity: ToastSeverity;
}

const displayNameFor = (editor: EditorRecord) => (editor.name || '').trim() || editor.email;

const MyEditors: React.FC = () => {
  const { user } = useAuth();
  const limit = EDITOR_CONFIG.limits.maxEditorsPerUser;

  const [editors, setEditors] = useState<EditorRecord[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Invite
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFieldError, setInviteFieldError] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);

  // Row actions
  const [menu, setMenu] = useState<{ anchor: HTMLElement; editor: EditorRecord } | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);

  // Remove
  const [removeTarget, setRemoveTarget] = useState<EditorRecord | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'success' });
  const showToast = (message: string, severity: ToastSeverity = 'success') => setToast({ open: true, message, severity });

  const loadEditors = useCallback(async () => {
    if (!user) {
      debugLog('User not authenticated yet, skipping editor load');
      setListLoading(false);
      return;
    }
    setListLoading(true);
    try {
      const fetched = await fetchEditors();
      setEditors(Array.isArray(fetched) ? fetched : []);
      setLoadFailed(false);
      setHasLoaded(true);
    } catch (error) {
      // An outage must not read as "you have no editors".
      console.error('Error loading editors:', error);
      setEditors([]);
      setLoadFailed(true);
    } finally {
      setListLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadEditors();
  }, [loadEditors]);

  const summary = useMemo(() => summarizeEditors(editors, limit), [editors, limit]);
  const inviterName = user?.displayName || user?.email || 'PithPlay User';

  // ---- Invite ----------------------------------------------------------------

  const openInvite = () => {
    if (isAtEditorLimit(editors.length, limit)) {
      showToast(`You've reached the limit of ${limit} editors. Remove one to invite someone new.`, 'warning');
      return;
    }
    setInviteEmail('');
    setInviteFieldError(null);
    setInviteError(null);
    setInviteOpen(true);
  };

  const closeInvite = () => {
    if (inviting) return;
    setInviteOpen(false);
  };

  const handleInviteConfirm = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (inviting) return;

    const email = inviteEmail.trim();
    const fieldError = validateInviteEmail(email, user?.email);
    if (fieldError) {
      setInviteFieldError(fieldError);
      return;
    }
    if (!user) {
      setInviteError(SESSION_ENDED);
      return;
    }

    setInviting(true);
    setInviteError(null);
    try {
      const token = await user.getIdToken(true);
      if (!token) {
        setInviteError(SESSION_ENDED);
        return;
      }

      // The editor sets their own name when they join; the email's local
      // part stands in until then.
      const temporaryName = email.split('@')[0];
      const updatedEditor = await addOrUpdateEditor(email, temporaryName, token);
      const invitationLink = buildInvitationLink(updatedEditor?.status, updatedEditor?.invitationToken, email);

      // Await the send and report it honestly: a rate-limited or bounced
      // invitation must not read as sent.
      let emailDelivered = false;
      let emailError: unknown;
      try {
        const emailResult = await sendProfessionalEditorInvitation(email, temporaryName, inviterName, invitationLink);
        emailDelivered = Boolean(emailResult?.success);
        emailError = emailResult?.error;
      } catch (sendError) {
        emailError = sendError;
      }

      await loadEditors();
      setInviteOpen(false);
      setInviteEmail('');

      if (emailDelivered) {
        showToast(`Invitation sent to ${email}.`);
      } else {
        const reason = readableEditorError(emailError, '');
        showToast(
          `${email} was added, but we couldn't send the invitation email${reason ? ` (${reason})` : ''}. Use Resend invitation to try again.`,
          'warning'
        );
      }
    } catch (error) {
      console.error('Error inviting editor:', error);
      if (isSelfInvitationError(error)) {
        setInviteFieldError("You can't invite yourself.");
        return;
      }
      setInviteError(readableEditorError(error, `We couldn't invite ${email}. Try again.`));
    } finally {
      setInviting(false);
    }
  };

  // ---- Resend ----------------------------------------------------------------

  const handleResendInvitation = async (editor: EditorRecord) => {
    setMenu(null);
    if (!user) {
      showToast(SESSION_ENDED, 'warning');
      return;
    }
    setResendingId(editor.id);
    try {
      const token = await user.getIdToken(true);
      if (!token) {
        showToast(SESSION_ENDED, 'error');
        return;
      }

      // A fresh invitation token each time, so an old link stops working.
      const updatedEditor = await addOrUpdateEditor(editor.email, editor.name, token);
      const invitationLink = buildInvitationLink(updatedEditor?.status, updatedEditor?.invitationToken, editor.email);
      const emailResult = await sendProfessionalEditorInvitation(editor.email, editor.name, inviterName, invitationLink);

      if (!emailResult?.success) {
        throw new Error(emailResult?.error || '');
      }
      showToast(`Invitation sent again to ${editor.email}. The new link expires in 7 days.`);
      await loadEditors();
    } catch (error) {
      console.error('Error resending invitation:', error);
      showToast(readableEditorError(error, `We couldn't resend the invitation to ${editor.email}. Try again.`), 'error');
    } finally {
      setResendingId(null);
    }
  };

  // ---- Remove ----------------------------------------------------------------

  const openRemove = (editor: EditorRecord) => {
    setMenu(null);
    setRemoveError(null);
    setRemoveTarget(editor);
  };

  const closeRemove = () => {
    if (removing) return;
    setRemoveTarget(null);
  };

  const handleRemoveConfirm = async () => {
    if (!removeTarget || removing) return;
    if (!user) {
      setRemoveError(SESSION_ENDED);
      return;
    }
    const target = removeTarget;
    setRemoving(true);
    setRemoveError(null);
    try {
      const token = await user.getIdToken(true);
      if (!token) {
        setRemoveError(SESSION_ENDED);
        return;
      }
      await removeEditor(target.id, token);
      setEditors((current) => current.filter((editor) => editor.id !== target.id));
      setRemoveTarget(null);
      showToast(`${displayNameFor(target)} removed.`);
    } catch (error) {
      console.error('Error removing editor:', error);
      setRemoveError(readableEditorError(error, "We couldn't remove this editor. Try again."));
    } finally {
      setRemoving(false);
    }
  };

  // ---- Render ----------------------------------------------------------------

  const renderRow = (editor: EditorRecord, index: number) => {
    const meta = getInvitationStatusMeta(editor.status);
    const name = displayNameFor(editor);
    const showEmail = name !== editor.email;
    const resending = resendingId === editor.id;
    const menuOpen = menu?.editor.id === editor.id;
    return (
      <Box
        component="li"
        key={editor.id}
        data-testid={`editor-row-${editor.id}`}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '36px minmax(0, 1fr) 40px', md: '36px minmax(0, 1fr) 150px 40px' },
          gridTemplateAreas: { xs: '"avatar who menu" ". status ."', md: '"avatar who status menu"' },
          columnGap: 2,
          rowGap: 1,
          alignItems: 'center',
          px: 2.5,
          py: 1.75,
          borderTop: index === 0 ? 0 : 1,
          borderColor: 'divider',
        }}
      >
        <Avatar
          aria-hidden="true"
          sx={{
            gridArea: 'avatar',
            width: 36,
            height: 36,
            fontSize: '0.875rem',
            fontWeight: 700,
            bgcolor: 'grey.200',
            color: brand.slate,
          }}
        >
          {name.charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ gridArea: 'who', minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem' }} noWrap>
            {name}
          </Typography>
          {showEmail && (
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {editor.email}
            </Typography>
          )}
        </Box>
        <Box sx={{ gridArea: 'status', justifySelf: 'start' }}>
          <StatusChip label={meta.label} tone={meta.tone} />
        </Box>
        <Box sx={{ gridArea: 'menu', justifySelf: 'end' }}>
          <IconButton
            size="small"
            aria-label={resending ? `Sending invitation to ${name}` : `More actions for ${name}`}
            aria-haspopup="menu"
            aria-expanded={menuOpen ? 'true' : undefined}
            aria-controls={menuOpen ? 'editor-actions-menu' : undefined}
            aria-busy={resending || undefined}
            disabled={resending}
            onClick={(event) => setMenu({ anchor: event.currentTarget, editor })}
          >
            {resending ? <CircularProgress size={18} /> : <MoreVertOutlinedIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
    );
  };

  const renderBody = () => {
    if (loadFailed) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadEditors} disabled={listLoading}>
              Retry
            </Button>
          }
        >
          {LOAD_ERROR}
        </Alert>
      );
    }
    if (listLoading && !hasLoaded) {
      return <LoadingState variant="list" count={3} label="Loading editors" />;
    }
    if (editors.length === 0) {
      return (
        <Card>
          <EmptyState
            icon={<PersonAddAltOutlinedIcon />}
            title="No editors yet"
            description={`Invite the editors you work with. ${INVITE_COPY}`}
            actionLabel="Invite an editor"
            onAction={openInvite}
          />
        </Card>
      );
    }
    return (
      <Section disablePadding>
        <Box component="ul" aria-label="Your editors" aria-busy={listLoading || undefined} sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {editors.map(renderRow)}
        </Box>
      </Section>
    );
  };

  const menuEditor = menu?.editor;

  return (
    <PageContainer>
      <PageHeader
        title="Editors"
        subtitle="Invite editors and manage who can work on your projects."
        meta={hasLoaded && !loadFailed ? <span data-testid="editors-summary">{formatEditorSummary(summary, limit)}</span> : undefined}
        action={
          <>
            <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={loadEditors} disabled={listLoading}>
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddAltOutlinedIcon />}
              onClick={openInvite}
              disabled={listLoading || loadFailed}
            >
              Invite editor
            </Button>
          </>
        }
      />

      {renderBody()}

      <Menu
        id="editor-actions-menu"
        anchorEl={menu?.anchor ?? null}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {menuEditor && canResendInvitation(menuEditor.status) && (
          <MenuItem onClick={() => handleResendInvitation(menuEditor)}>
            <ListItemIcon>
              <SendOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Resend invitation</ListItemText>
          </MenuItem>
        )}
        {menuEditor && (
          <MenuItem onClick={() => openRemove(menuEditor)} sx={{ color: 'error.main' }}>
            <ListItemIcon sx={{ color: 'inherit' }}>
              <PersonRemoveOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Remove editor</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Invite */}
      <Dialog open={inviteOpen} onClose={closeInvite} maxWidth="xs" fullWidth aria-labelledby="invite-editor-title">
        <form onSubmit={handleInviteConfirm} noValidate>
          <DialogTitle id="invite-editor-title">Invite an editor</DialogTitle>
          <DialogContent>
            <DialogContentText variant="body2" sx={{ mb: 2 }}>
              {INVITE_COPY}
            </DialogContentText>
            {inviteError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {inviteError}
              </Alert>
            )}
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              id="invite-editor-email"
              type="email"
              label="Email address"
              placeholder="editor@example.com"
              // Not "email": autofill would offer the creator's own address.
              autoComplete="off"
              value={inviteEmail}
              onChange={(event) => {
                setInviteEmail(event.target.value);
                if (inviteFieldError) setInviteFieldError(null);
              }}
              error={Boolean(inviteFieldError)}
              helperText={inviteFieldError || "They'll choose their own name when they join."}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={closeInvite} disabled={inviting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={inviting}
              startIcon={inviting ? <CircularProgress size={16} color="inherit" /> : <SendOutlinedIcon />}
            >
              {inviting ? 'Sending…' : 'Send invite'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Remove */}
      <Dialog open={Boolean(removeTarget)} onClose={closeRemove} maxWidth="xs" fullWidth aria-labelledby="remove-editor-title">
        <DialogTitle id="remove-editor-title">{removeTarget ? `Remove ${displayNameFor(removeTarget)}?` : 'Remove editor?'}</DialogTitle>
        <DialogContent>
          {removeError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {removeError}
            </Alert>
          )}
          <DialogContentText variant="body2">
            They&apos;ll lose access to your projects, and any project they haven&apos;t finished goes back to unassigned so you can choose another editor. To work with them again, you&apos;ll need to send a new invitation.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeRemove} disabled={removing}>
            Cancel
          </Button>
          <Button
            onClick={handleRemoveConfirm}
            variant="contained"
            color="error"
            disabled={removing}
            startIcon={removing ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {removing ? 'Removing…' : 'Remove editor'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={toast.severity === 'success' ? 5000 : 10000}
        onClose={(_, reason) => {
          if (reason !== 'clickaway') setToast((prev) => ({ ...prev, open: false }));
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setToast((prev) => ({ ...prev, open: false }))} severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default MyEditors;
